import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';

import GamePage from './features/game/GamePage';
import PlayerNameSetup from './features/player/PlayerNameSetup';
import { MatchFoundData, PlayerColor } from '../../shared/types/game';
import MatchingScreen from './features/matchmaking/MatchingScreen';
import MyPage from './features/mypage/MyPage';
import AuthPage from './features/auth/AuthPage';

function App() {
  const [playerName, setPlayerName] = useState<string | null>(null);
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [isRegistered, setIsRegistered] = useState<boolean>(false);

  const socketRef = useRef<Socket | null>(null);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [isPlayerConnectedToServer, setIsPlayerConnectedToServer] = useState(false);
  const [authTrigger, setAuthTrigger] = useState(0);

  // マッチング成立時にGamePageに渡すための状態
  const [currentRoomId, setCurrentRoomId] = useState<string | null>(null);
  const [myColor, setMyColor] = useState<PlayerColor | null>(null);
  const [opponentPlayerName, setOpponentPlayerName] = useState<string | null>(null);
  const [opponentPlayerId, setOpponentPlayerId] = useState<string | null>(null);
  const [initialTimeMs, setInitialTimeMs] = useState<number | null>(null);

  const navigate = useNavigate();

  // ログアウト処理
  const handleLogout = useCallback(() => {
    console.log('App: ログアウト処理を開始します。');
    localStorage.removeItem('shogiAuthToken');
    localStorage.removeItem('shogiPlayerId');
    localStorage.removeItem('shogiPlayerName');
    localStorage.removeItem('shogiRoomId');
    localStorage.removeItem('shogiMyColor');
    localStorage.removeItem('shogiOpponentPlayerId');
    localStorage.removeItem('shogiOpponentPlayerName');
    localStorage.removeItem('shogiInitialTimeMs');

    setPlayerName(null);
    setPlayerId(null);
    setIsRegistered(false);
    setIsPlayerConnectedToServer(false);
    setIsSocketConnected(false);

    if (socketRef.current) {
      console.log('App: ログアウト時に既存のWebSocket接続を強制切断します。');
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    setAuthTrigger(prev => prev + 1);
    navigate('/');
  }, [navigate]);

  useEffect(() => {
    const storedAuthToken = localStorage.getItem('shogiAuthToken');
    const storedPlayerIdFromLs = localStorage.getItem('shogiPlayerId');
    const storedPlayerNameFromLs = localStorage.getItem('shogiPlayerName');

    setIsRegistered(!!storedAuthToken);

    if (socketRef.current) {
      console.log('App: 既存のWebSocket接続をクリーンアップします。');
      socketRef.current.off('connect');
      socketRef.current.off('player:id_assigned');
      socketRef.current.off('disconnect');
      socketRef.current.off('connect_error');
      socketRef.current.off('auth:error');
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    console.log('App: 新しいWebSocket接続を確立します。');
    const socket = io({
      auth: {
        token: storedAuthToken,
        playerId: storedPlayerIdFromLs,
        playerName: storedPlayerNameFromLs,
      },
      reconnectionAttempts: 5, 
      reconnectionDelay: 1000,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('App: WebSocketに接続しました。');
      setIsSocketConnected(true);
      
      const idToEmit = storedPlayerIdFromLs || '';
      const nameToEmit = storedPlayerNameFromLs || '';
      
      console.log(`App: サーバーにplayer:connectedを送信します。ID: ${idToEmit}, Name: ${nameToEmit}`);
      socket.emit('player:connected', { playerId: idToEmit, playerName: nameToEmit });
    });

    socket.on('player:id_assigned', (id: string) => {
      console.log(`App: サーバーからplayer:id_assignedを受信: ${id}`);
      setPlayerId(id);
      const currentName = localStorage.getItem('shogiPlayerName');
      if (currentName) setPlayerName(currentName);
      localStorage.setItem('shogiPlayerId', id);
      setIsPlayerConnectedToServer(true);
    });

    socket.on('disconnect', (reason) => {
      console.log(`App: WebSocketから切断しました。理由: ${reason}`);
      setIsSocketConnected(false);
      setIsPlayerConnectedToServer(false);
    });

    socket.on('connect_error', (error) => {
      console.error('App: WebSocket接続エラー:', error);
      setIsSocketConnected(false);
      setIsPlayerConnectedToServer(false);
      if (storedAuthToken) {
        console.warn('App: 認証トークンがありますが、接続エラーが発生しました。再接続を試みています...');
      }
    });

    socket.on('auth:error', (data: { message: string }) => {
      console.error(`App: 認証エラー: ${data.message}`);
      handleLogout();
      navigate('/auth'); 
    });

    return () => {
      console.log('App: WebSocketクリーンアップを実行します。');
      if (socketRef.current) {
        socketRef.current.off('connect');
        socketRef.current.off('player:id_assigned');
        socketRef.current.off('disconnect');
        socketRef.current.off('connect_error');
        socketRef.current.off('auth:error');
        socketRef.current = null;
      }
    };
  }, [
    authTrigger,
    handleLogout
  ]);

  useEffect(() => {
    const storedPlayerName = localStorage.getItem('shogiPlayerName');
    const storedPlayerId = localStorage.getItem('shogiPlayerId');
    if (storedPlayerName && storedPlayerName !== playerName) setPlayerName(storedPlayerName);
    if (storedPlayerId && storedPlayerId !== playerId) setPlayerId(storedPlayerId);

    const currentPath = window.location.pathname;

    if (isSocketConnected && isPlayerConnectedToServer) {
      const storedRoomId = localStorage.getItem('shogiRoomId');
      const storedMyColor = localStorage.getItem('shogiMyColor') as PlayerColor | null;
      const storedOpponentPlayerId = localStorage.getItem('shogiOpponentPlayerId');
      const storedOpponentPlayerName = localStorage.getItem('shogiOpponentPlayerName');
      const storedInitialTimeMs = localStorage.getItem('shogiInitialTimeMs');

      // 進行中のゲームに再接続
      if (storedRoomId && storedMyColor && storedOpponentPlayerName && storedOpponentPlayerId && storedInitialTimeMs) {
        setCurrentRoomId(storedRoomId);
        setMyColor(storedMyColor);
        setOpponentPlayerName(storedOpponentPlayerName);
        setOpponentPlayerId(storedOpponentPlayerId);
        setInitialTimeMs(parseInt(storedInitialTimeMs, 10));
        if (currentPath !== '/game') {
          console.log('App: 進行中のゲームが存在するため /game に遷移します。');
          navigate('/game');
        }
      } 
      // 認証済みユーザーの場合、/matching へ
      else if (isRegistered) {
        if (currentPath === '/' || currentPath === '/auth') {
          console.log('App: 認証済みユーザーです。/matching に遷移します。');
          navigate('/matching');
        }
      }
      // 匿名ユーザーで名前が設定されている場合
      else if (playerId && playerName) {
        if (currentPath === '/') { // /auth の条件を削除
          console.log('App: 匿名ユーザーで名前が設定済みです。/matching に遷移します。');
          navigate('/matching');
        }
      } 
      // 匿名ユーザーで名前が未設定の場合、/ へ
      else {
        if (currentPath !== '/auth' && currentPath !== '/') {
          console.log('App: プレイヤー情報が不足しています。/ に遷移します。');
          navigate('/');
        }
      }
    } else {
      // ソケットは接続されているが、プレイヤー情報がサーバーに認識されていない場合
      if (isSocketConnected && currentPath !== '/' && currentPath !== '/auth') {
        console.log("App: isSocketConnectedはtrueですが、isPlayerConnectedToServerがfalseです。プレイヤー情報の同期を待機中。");
        return; 
      }
      // ソケットが完全に接続されていない場合（isSocketConnectedがfalse）またはプレイヤー情報がない場合
      if (currentPath !== '/' && currentPath !== '/auth') {
        console.log("App: ソケット未接続またはプレイヤー情報がありません。/ に遷移します。");
        navigate('/');
      }
    }
  }, [navigate, isSocketConnected, isPlayerConnectedToServer, playerId, playerName, isRegistered]);

  const handleNameSet = useCallback((name: string, id: string) => {
    setPlayerName(name);
    setPlayerId(id);
    localStorage.setItem('shogiPlayerId', id);
    localStorage.setItem('shogiPlayerName', name); 
    // 名前設定後、isRegisteredの状態を確認し、適切なページへ遷移
    const storedAuthToken = localStorage.getItem('shogiAuthToken');
    if (storedAuthToken) {
      setIsRegistered(true);
      navigate('/matching'); // 登録済みならマッチングへ
    } else {
      setIsRegistered(false);
      navigate('/matching'); // 匿名ならマッチングへ
    }
  }, [navigate]);

  const handleMatchFound = useCallback((data: MatchFoundData) => {
    setCurrentRoomId(data.roomId);
    localStorage.setItem('shogiRoomId', data.roomId);
    setMyColor(data.yourColor);
    localStorage.setItem('shogiMyColor', data.yourColor);
    setInitialTimeMs(data.initialTimeMs);
    localStorage.setItem('shogiInitialTimeMs', data.initialTimeMs.toString());

    if (data.yourColor === 'sente') {
      setOpponentPlayerName(data.gotePlayer.name);
      localStorage.setItem('shogiOpponentPlayerName', data.gotePlayer.name);
      setOpponentPlayerId(data.gotePlayer.id);
      localStorage.setItem('shogiOpponentPlayerId', data.gotePlayer.id);
    } else {
      setOpponentPlayerName(data.sentePlayer.name);
      localStorage.setItem('shogiOpponentPlayerName', data.sentePlayer.name);
      setOpponentPlayerId(data.sentePlayer.id);
      localStorage.setItem('shogiOpponentPlayerId', data.sentePlayer.id);
    }
    navigate('/game');
  }, [navigate]);

  const handlePlayerNameUpdate = useCallback((newName: string) => {
    setPlayerName(newName);
    setIsRegistered(!!localStorage.getItem('shogiAuthToken')); 
  }, []);

  const handleAuthSuccess = useCallback(() => {
    console.log("App: Auth success detected. Triggering socket re-initialization and updating state for registered user.");
    // 認証成功時、localStorageにトークンとプレイヤー情報が設定されているはずなので、stateを更新
    const storedAuthToken = localStorage.getItem('shogiAuthToken');
    const storedPlayerIdFromLs = localStorage.getItem('shogiPlayerId');
    const storedPlayerNameFromLs = localStorage.getItem('shogiPlayerName');

    if (storedAuthToken && storedPlayerIdFromLs && storedPlayerNameFromLs) {
      setIsRegistered(true);
      setPlayerId(storedPlayerIdFromLs);
      setPlayerName(storedPlayerNameFromLs);
      // socketを再接続して、サーバーに新しい認証情報で接続させる
      setAuthTrigger(prev => prev + 1); 
      navigate('/matching'); // 認証成功後はマッチング画面へ
    } else {
      console.error("App: 認証成功したが、必要な情報がlocalStorageに見つかりません。");
      handleLogout(); // エラーがあればログアウト
    }
  }, [navigate, handleLogout]);

  const currentPath = window.location.pathname;
  const socketInstance: Socket | null = socketRef.current; 

  // ソケット接続中の待機画面
  if (!isSocketConnected && currentPath !== '/' && currentPath !== '/auth') {
    return (
      <div className="App">
        <header className="App-header">
          <h1>オンライン将棋</h1>
        </header>
        <main>
          <p>サーバーに接続中...</p>
        </main>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1>オンライン将棋</h1>
      </header>
      <main>
        <Routes>
          <Route
            path="/"
            element={
              (isRegistered || (playerName && playerId)) ? (
                <p>リダイレクト中...</p>
              ) : (
                <PlayerNameSetup
                  socket={socketInstance}
                  onNameSet={handleNameSet}
                  isSocketConnected={isSocketConnected}
                  isPlayerConnectedToServer={isPlayerConnectedToServer}
                />
              )
            }
          />
          <Route
            path="/matching"
            element={
              playerName && playerId ? (
                <MatchingScreen
                  playerName={playerName}
                  playerId={playerId}
                  onMatchFound={handleMatchFound}
                  socket={socketInstance}
                  isRegistered={isRegistered}
                  onLogout={handleLogout}
                  isSocketConnected={isSocketConnected}
                  isPlayerConnectedToServer={isPlayerConnectedToServer}
                />
              ) : (
                <p>プレイヤー情報がありません。名前設定画面に戻ってください。</p>
              )
            }
          />
          <Route
            path="/game"
            element={
              playerName && playerId && currentRoomId && myColor && opponentPlayerName && opponentPlayerId && initialTimeMs !== null ? (
                <GamePage
                  playerName={playerName}
                  playerId={playerId}
                  roomId={currentRoomId}
                  myColor={myColor}
                  opponentPlayerName={opponentPlayerName}
                  opponentPlayerId={opponentPlayerId}
                  initialTimeMs={initialTimeMs}
                  socket={socketInstance!}
                />
              ) : (
                <p>ゲーム情報がありません。マッチング画面に戻ってください。</p>
              )
            }
          />
          <Route
            path="/mypage"
            element={
              playerId && playerName ? (
                <MyPage
                  playerId={playerId}
                  playerName={playerName}
                  onNameUpdate={handlePlayerNameUpdate}
                  socket={socketInstance}
                  isRegistered={isRegistered}
                  onLogout={handleLogout}
                  isSocketConnected={isSocketConnected}
                  isPlayerConnectedToServer={isPlayerConnectedToServer}
                />
              ) : (
                <p>プレイヤー情報がありません。名前設定画面に戻ってください。</p>
              )
            }
          />
          <Route
            path="/auth"
            element={
              isRegistered ? (
                <p>認証済みです。マッチング画面へリダイレクト中...</p>
              ) : (
                <AuthPage
                  socket={socketInstance}
                  onAuthSuccess={handleAuthSuccess}
                  isSocketConnected={isSocketConnected}
                  isPlayerConnectedToServer={isPlayerConnectedToServer}
                />
              )
            }
          />
          <Route path="*" element={<div>404 Not Found</div>} />
        </Routes>
      </main>
    </div>
  );
}

export default App;